/* eslint-disable jira/naming/disallow-filenames */
// eslint-disable-next-line jira/filename-case
import React, { useCallback, useState } from 'react';
import MenuGroup from '@atlaskit/menu/menu-group';
import Section from '@atlaskit/menu/section';
import ButtonItem from '@atlaskit/menu/button-item';
import { Text } from '@atlaskit/primitives/compiled/text';
import Tooltip from '@atlaskit/tooltip/Tooltip';
import { useIntl, defineMessage } from '@atlassian/jira-intl';
import { MoreNavMenuButton } from '@jira/navigation-apps__sidebar-nav4-sidebars-common-core/src/ui/more-nav-menu-button/main.tsx';
import { isFakeId } from '@jira/polaris__component-navigation-store/src/updateContainerState.tsx';
import { useNavigationActions } from '@jira/polaris__component-navigation-store/src/useNavigationActions.tsx';
import { DELETE_VIEW, RENAME_VIEW } from '../../../../../common/constants/analytics/item-id.tsx';
import type { MenuProps } from '../types.tsx';
import { DeleteModal } from '../../../../delete-modal/index.tsx';
import { ActionButtonCreateView } from '../../../../action-button-create-view/index.tsx';
import { useAnalyticsL3, useAnalyticsL4 } from '../../../../../controllers/context.tsx';

const renameAction = defineMessage({
	defaultMessage: 'Rename',
	id: 'navigation-apps-sidebar-nav4-sidebars-common-jpd-nav-views.menu-items.views.viewset.menu.rename-action',
	description: 'Label for the button to rename the selected view.',
});

const deleteAction = defineMessage({
	defaultMessage: 'Delete',
	id: 'navigation-apps-sidebar-nav4-sidebars-common-jpd-nav-views.menu-items.views.viewset.menu.delete-action',
	description: 'Label for deleting the selected view. Displayed in the sidebar.',
});

const onDeleteTooltip = defineMessage({
	id: 'navigation-apps-sidebar-nav4-sidebars-common-jpd-nav-views.menu-items.views.viewset.menu.on-delete-tooltip',
	defaultMessage:
		"You can't delete a section that contains views. Please remove all views from this section to delete it",
	description:
		'Tooltip shown when attempting to delete a viewset section that still contains views.',
});

type NavMenuItemsProps = {
	onDelete: () => void;
	onRename: () => void;
	viewsetContainsViews: boolean;
};

const NavMenuItems = ({ onDelete, onRename, viewsetContainsViews }: NavMenuItemsProps) => {
	const { formatMessage } = useIntl();

	return (
		<MenuGroup minWidth={250}>
			<Section isList>
				<ButtonItem onClick={onRename}>{formatMessage(renameAction)}</ButtonItem>
			</Section>
			<Section isList hasSeparator>
				<Tooltip content={viewsetContainsViews ? formatMessage(onDeleteTooltip) : ''}>
					<ButtonItem onClick={onDelete} isDisabled={viewsetContainsViews}>
						{viewsetContainsViews ? (
							formatMessage(deleteAction)
						) : (
							<Text color="color.text.danger">{formatMessage(deleteAction)}</Text>
						)}
					</ButtonItem>
				</Tooltip>
			</Section>
		</MenuGroup>
	);
};

export const Menu = ({ view, navigationContainer }: MenuProps): React.JSX.Element | null => {
	const { deleteViewSet, renameViewSet } = useNavigationActions();
	const { sendMeatBallDropDownOnViewsMenuItemAnalyticsEvent } = useAnalyticsL4();
	const { sendMeatBallDropDownOnViewsAnalyticsEvent } = useAnalyticsL3();

	const [isDeleteConfirmationModalOpen, setDeleteConfirmationModalOpen] = useState(false);

	const handleNavMenuOpen = useCallback(() => {
		sendMeatBallDropDownOnViewsAnalyticsEvent({
			container: navigationContainer,
			viewParams: {
				viewKind: view.viewType,
				viewId: view.id,
			},
		});
	}, [navigationContainer, sendMeatBallDropDownOnViewsAnalyticsEvent, view.id, view.viewType]);

	const viewsetContainsViews = Boolean(view.views?.length);

	const handleDelete = useCallback(() => {
		sendMeatBallDropDownOnViewsMenuItemAnalyticsEvent({
			itemId: DELETE_VIEW,
			viewParams: {
				viewKind: view.viewType,
				viewId: view.id,
			},
			container: navigationContainer,
		});
		setDeleteConfirmationModalOpen(true);
	}, [
		navigationContainer,
		sendMeatBallDropDownOnViewsMenuItemAnalyticsEvent,
		view.id,
		view.viewType,
	]);

	const handleRename = useCallback(() => {
		sendMeatBallDropDownOnViewsMenuItemAnalyticsEvent({
			itemId: RENAME_VIEW,
			viewParams: {
				viewKind: view.viewType,
				viewId: view.id,
			},
			container: navigationContainer,
		});
		renameViewSet(view.localId);
	}, [
		navigationContainer,
		renameViewSet,
		sendMeatBallDropDownOnViewsMenuItemAnalyticsEvent,
		view.id,
		view.localId,
		view.viewType,
	]);

	const MenuItems = useCallback(
		() => (
			<NavMenuItems
				onDelete={handleDelete}
				onRename={handleRename}
				viewsetContainsViews={viewsetContainsViews}
			/>
		),
		[handleDelete, handleRename, viewsetContainsViews],
	);

	const handleDeleteModalCancel = useCallback(() => {
		setDeleteConfirmationModalOpen(false);
	}, []);

	const handleDeleteModalConfirm = useCallback(() => {
		setDeleteConfirmationModalOpen(false);
		deleteViewSet(view.localId);
	}, [deleteViewSet, view.localId]);

	if (isFakeId(view.id)) {
		return null;
	}

	return (
		<>
			<ActionButtonCreateView
				navigationContainer={navigationContainer}
				container={view.localId}
				sectionId={view.id}
			/>
			<MoreNavMenuButton onOpen={handleNavMenuOpen} MenuItems={MenuItems} name={view.name} />
			<DeleteModal
				title={view.name}
				isOpen={isDeleteConfirmationModalOpen}
				onCancel={handleDeleteModalCancel}
				onConfirm={handleDeleteModalConfirm}
			/>
		</>
	);
};
